import { useEffect, useRef, useState } from 'react';
import { Button, Input } from 'antd';
import type { InputRef } from 'antd';
import {
  ForgotPasswordMethod,
  IOTPTimerProps,
  IOTPCounterProps,
  IOTPLockProps,
} from 'src/shared/interface/forgot-password';
import { OTP_LENGTH } from 'src/shared/constants';
import { formatTime } from '../helper';
import { ImMobile } from 'react-icons/im';
import { MdOutlineMail } from 'react-icons/md';
import clsx from 'clsx';

interface IOTPInputProps {
  method: ForgotPasswordMethod;
  identifier: string;
  timers: IOTPTimerProps;
  counters: IOTPCounterProps;
  locks: IOTPLockProps;
  loading: boolean;
  canResend: boolean;
  onOTPComplete: (otp: string) => void;
  onResend: () => Promise<void>;
  onBack: () => void;
}

export const OTPInput = ({
  method,
  identifier,
  timers,
  counters,
  locks,
  loading,
  canResend,
  onOTPComplete,
  onResend,
  onBack,
}: IOTPInputProps) => {
  const { otpExpiry, resendCooldown, verifyLock, resendLock } = timers;
  const { verifyAttempts, remainingVerifyAttempts, currentResendCount, maxResendCount } = counters;
  const { isVerifyLocked, isResendLocked } = locks;

  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef<(InputRef | null)[]>([]);
  const prevAttemptsRef = useRef(verifyAttempts);

  useEffect(() => {
    if (!isVerifyLocked) inputRefs.current[0]?.focus();
  }, [isVerifyLocked]);

  // Clear OTP when verify attempts change
  useEffect(() => {
    if (verifyAttempts > prevAttemptsRef.current) {
      setOtp(Array(OTP_LENGTH).fill(''));
      setTimeout(() => inputRefs.current[0]?.focus(), 0);
    }
    prevAttemptsRef.current = verifyAttempts;
  }, [verifyAttempts]);

  const handleChange = (index: number, value: string) => {
    if (isVerifyLocked || loading) return;
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newOtp.every((digit) => digit !== '') && value) {
      onOTPComplete(newOtp.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isVerifyLocked || loading) return;

    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else {
        const newOtp = [...otp];
        newOtp[index] = '';
        setOtp(newOtp);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    if (isVerifyLocked || loading) return;

    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    if (/^\d{1,6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      const newOtp = [...otp];

      digits.forEach((digit, index) => {
        if (index < OTP_LENGTH) newOtp[index] = digit;
      });

      setOtp(newOtp);

      const lastIndex = Math.min(digits.length - 1, OTP_LENGTH - 1);
      inputRefs.current[lastIndex]?.focus();

      if (digits.length === OTP_LENGTH) {
        onOTPComplete(newOtp.join(''));
      }
    }
  };

  const handleResend = async () => {
    if (!canResend || isResending) return;

    setIsResending(true);
    setOtp(Array(OTP_LENGTH).fill(''));

    try {
      await onResend();
    } catch (error: any) {
      console.error('Resend OTP error:', error);
      setOtp(Array(OTP_LENGTH).fill(''));
    } finally {
      setIsResending(false);
      inputRefs.current[0]?.focus();
    }
  };

  const destinationType = method === ForgotPasswordMethod.EMAIL ? 'email' : 'SĐT';
  const isOutOfResends = currentResendCount >= maxResendCount;

  const resendCountdown = resendLock > 0 ? resendLock : resendCooldown > 0 ? resendCooldown : 0;

  const isResendDisabled = !canResend || isResending || resendCountdown > 0;

  return (
    <div className='w-full'>
      {/* Header */}
      <div className='mb-6 text-center'>
        <div className='mb-2 flex justify-center'>
          <span className='flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-3'>
            {method === ForgotPasswordMethod.EMAIL ? (
              <MdOutlineMail className='text-3xl' />
            ) : (
              <ImMobile className='text-3xl' />
            )}
          </span>
        </div>
        <h3 className='text-lg font-semibold mb-2'>Nhập mã xác thực</h3>
        <p className='text-gray-600 text-sm'>
          Mã xác thực đã được gửi đến {destinationType}:{' '}
          <span className='font-semibold text-gray-800'>{identifier}</span>
        </p>
      </div>

      {/* Content */}
      {isVerifyLocked ? (
        <div className='mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-center'>
          <p className='text-red-600 font-semibold mb-1'>Tạm dừng nhập OTP</p>
          <p className='text-red-500 text-sm'>
            Bạn đã nhập sai quá nhiều lần. Vui lòng thử lại sau {formatTime(verifyLock)}
          </p>
        </div>
      ) : (
        <>
          <div className='mb-4'>
            <div className='flex justify-center gap-3 mb-4'>
              {otp.map((digit, index) => (
                <Input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  maxLength={1}
                  disabled={isVerifyLocked || loading}
                  className='w-12 h-14 text-center text-2xl font-bold'
                  style={{ fontSize: '24px', fontWeight: 'bold' }}
                />
              ))}
            </div>

            {/* Timer */}
            <div className='text-center mb-2'>
              <span className='text-gray-600 text-sm'>
                Mã OTP hết hạn sau:{' '}
                <span
                  className={clsx('font-semibold', {
                    'text-red-600': otpExpiry <= 60,
                    'text-blue-600': otpExpiry > 60,
                  })}
                >
                  {formatTime(otpExpiry)}
                </span>
              </span>
            </div>

            {/* Attempts warning */}
            {verifyAttempts > 0 && (
              <div className='text-center mb-2'>
                <span className='text-orange-600 text-sm'>
                  Còn {remainingVerifyAttempts} lần thử
                </span>
              </div>
            )}
          </div>

          {/* Resend section */}
          <div className='mb-4 text-center'>
            <p className='text-gray-600 text-sm mb-2'>Không nhận được mã?</p>

            {isResendLocked || isOutOfResends ? (
              <div>
                <p className='text-red-500 text-sm font-medium'>
                  Đã hết lượt gửi OTP ({currentResendCount}/{maxResendCount})
                </p>
                {resendLock > 0 && (
                  <p className='text-red-400 text-xs mt-1'>Thử lại sau {formatTime(resendLock)}</p>
                )}
              </div>
            ) : (
              <>
                <Button
                  type='link'
                  onClick={handleResend}
                  disabled={isResendDisabled}
                  loading={isResending}
                  className='text-blue-600 font-medium'
                >
                  {isResending
                    ? resendCountdown > 0
                      ? `Gửi lại sau ${formatTime(resendCountdown)}`
                      : 'Đang gửi...'
                    : resendCountdown > 0
                    ? `Gửi lại sau ${formatTime(resendCountdown)}`
                    : 'Gửi lại mã'}
                </Button>
                {currentResendCount > 0 && (
                  <p className='text-gray-500 text-xs mt-1'>
                    Đã gửi {currentResendCount}/{maxResendCount} lần
                  </p>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Back button */}
      <div className='mt-6'>
        <Button type='link' onClick={onBack} className='w-full text-gray-600'>
          Quay lại
        </Button>
      </div>
    </div>
  );
};
